import { createContext, Dispatch, FC, ReactNode, SetStateAction, useCallback, useContext } from "react";

import { useUser } from "@/entities/Auth";
import { Tag } from "@/entities/Tag";
import { UserRole } from "@/shared/const/userRole";
import { handleError } from "@/shared/lib/helpers/utils";

import { TagData } from "../../types";
import { getTags, updateTagItem } from "../../utils/api";
import { useApi } from "../hooks/useApi";
import { useAlertContext } from "./alertContext";

type ContextType = {
  isLoading: boolean;
  data: Tag[];
  setData: Dispatch<SetStateAction<Tag[]>>;
  refreshList: () => void;
};

const TagsContext = createContext({} as ContextType);

interface Props {
  children: ReactNode;
}

const Context: FC<Props> = ({ children }) => {
  const { loading: isLoading, data, setData, refresh: refreshList } = useApi<Tag[]>(getTags, []);

  return (
    <TagsContext.Provider
      value={{
        isLoading,
        data,
        setData,
        refreshList,
      }}
    >
      {children}
    </TagsContext.Provider>
  );
};

export const useTagsList = () => {
  const { showMsg } = useAlertContext();

  const { isLoading, data, refreshList } = useContext(TagsContext);

  const updateItem = useCallback(
    async (id, data) => {
      const { error } = await updateTagItem(id, data as TagData);

      if (error) {
        const err = handleError(error);
        if (err.text) {
          return showMsg(error.text.join(", "));
        }
        return showMsg(error.detail || error);
      }

      await refreshList();
    },
    [showMsg, refreshList]
  );

  return {
    isLoading,
    data,
    updateItem,
    refreshList,
  };
};

const Wrapper: FC<Props> = ({ children }) => {
  const user = useUser();

  if (!user || user.role !== UserRole.Writer) {
    return <>{children}</>;
  }

  return <Context>{children}</Context>;
};

export default Wrapper;
